import { useTranslation } from "react-i18next";
import { FaCalendarAlt, FaRobot, FaUserCircle } from "react-icons/fa";
import { useTenant } from "../../providers/tenant";
import { Container } from "./styles";
import { IRegistrantEvent } from "../../providers/registrantEvents/registrantEvents.interface";
import { IEvent } from "../../providers/events/events.interface";
import Chart from "../Chart";
import { IUser } from "../../providers/users/user.interface";
import { useState, useEffect } from "react";
import { FormControl, MenuItem, Select } from "@mui/material";
import { BsFillCalendarCheckFill } from "react-icons/bs";
import { MdLiveTv } from "react-icons/md";
import { IEventSession } from "../../providers/eventSessions/eventsSessions.interface";
import Loading from "../Loading";
import { useUser } from "../../providers/users";

interface IEventFromDashboard extends IEvent {
  eventSessions: IEventSession[];
  registrationCount: number;
}

interface IChartItem {
  name: string;
  count: number;
}

interface ICharts {
  campaign: IChartItem[];
  utm_content: IChartItem[];
  utm_source: IChartItem[];
  utm_medium: IChartItem[];
}

const Dashboard = () => {
  const { t } = useTranslation();
  const { dashboardData, getDashboardData } = useTenant();
  const { apiLoading } = useUser();
  const [timePeriod, setTimePeriod] = useState<string>("Last 7 days");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("All");
  const [top5Events, setTop5Events] = useState<IEventFromDashboard[]>([]);

  const fetchRegistrantEvents = async (selectedTimePeriod: string) => {
    const daysLimit = selectedTimePeriod === "Last 7 days" ? 7 : 30;
    const timestamp = new Date(Date.now() - daysLimit * 86400000).getTime();
    await getDashboardData(timestamp, false);
  };

  const handleTimePeriodChange = (event: any) => {
    const selectedTimePeriod = event.target.value;
    setTimePeriod(selectedTimePeriod);
    fetchRegistrantEvents(selectedTimePeriod);
  };

  const getTopEventsWithRegistrationCounts = () => {
    const eventsWithCounts: IEventFromDashboard[] = dashboardData.events.filter(
      (event: IEvent) =>
        eventTypeFilter === "All" || event.eventType === eventTypeFilter
    );
    eventsWithCounts.forEach((event) => {
      event.registrationCount = event.eventSessions.find(
        (session: IEventSession) => session.sessionNumber === 1
      )?.registrationCount;
    });

    eventsWithCounts.sort((a, b) => b.registrationCount - a.registrationCount);
    return eventsWithCounts.slice(0, 5);
  };

  useEffect(() => {
    if (dashboardData) {
      const updatedTop5Events = getTopEventsWithRegistrationCounts();
      setTop5Events(updatedTop5Events);
    }
  }, [dashboardData, eventTypeFilter, timePeriod]);

  // Função para calcular a porcentagem para a barra de progresso
  const calculatePercentage = (item: IChartItem, data: IChartItem[]) => {
    if (data.length === 0) return 0;
    const totalCount = data.reduce((sum, current) => sum + current.count, 0);
    if (totalCount === 0) return 0;
    return Math.floor((item.count / totalCount) * 100);
  };

  if (apiLoading) {
    return <Loading />;
  }

  return (
    <Container>
      <div className="leftDiv">
        <section className="boxesDiv">
          <div className="bigBox">
            <div className="automatedEvents">
              <p>Automated Events Overview</p>
              <div>
                <FaCalendarAlt />
                <div>
                  <p>Events Created</p>
                  <span>{dashboardData.events.length}</span>
                </div>
              </div>
            </div>
            <div className="smallBoxes">
              <div>
                <MdLiveTv />
                <p>Live Now</p>
                <span>{dashboardData.liveNow}</span>
              </div>
              <div>
                <FaRobot />
                <p>AI Moderators</p>
                <span>{dashboardData.assistants.length}</span>
              </div>
            </div>
          </div>
        </section>
        <Chart
          registrations={dashboardData.registrantEvents}
          timePeriod={timePeriod}
        />
        <section className="registrantionMetrics">
          <div className="registrantionSources">
            <p>UTM Content</p>
            {dashboardData.charts?.utm_content.map((source) => {
              const percentage = calculatePercentage(
                source,
                dashboardData.charts.utm_content
              );
              return (
                <div className="sourceCards" key={source.name}>
                  <div className="outlineBar">
                    <div
                      className="inlineBar"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span>{percentage}%</span>
                  <p>{source.name}</p>
                </div>
              );
            })}
          </div>

          <div className="registrantionSources">
            <p>UTM Source</p>
            {dashboardData.charts?.utm_source.map((source) => {
              const percentage = calculatePercentage(
                source,
                dashboardData.charts.utm_source
              );
              return (
                <div className="sourceCards" key={source.name}>
                  <div className="outlineBar">
                    <div
                      className="inlineBar"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span>{percentage}%</span>
                  <p>{source.name}</p>
                </div>
              );
            })}
          </div>
        </section>
        {/* <section className="registrantionMetrics">
          <div className="registrantionSources">
            <p>Campaign Sources</p>
            {dashboardData.charts?.campaign.map((source) => {
              const percentage = calculatePercentage(
                source,
                dashboardData.charts.campaign
              );
              return (
                <div className="sourceCards" key={source.name}>
                  <div className="outlineBar">
                    <div
                      className="inlineBar"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span>{percentage}%</span>
                  <p>{source.name}</p>
                </div>
              );
            })}
          </div>

          <div className="registrantionSources">
            <p>UTM Medium</p>
            {dashboardData.charts?.utm_medium.map((source) => {
              const percentage = calculatePercentage(
                source,
                dashboardData.charts.utm_medium
              );
              return (
                <div className="sourceCards" key={source.name}>
                  <div className="outlineBar">
                    <div
                      className="inlineBar"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span>{percentage}%</span>
                  <p>{source.name}</p>
                </div>
              );
            })}
          </div>
        </section> */}
      </div>
      <div className="rightDiv">
        <h3>Top 5 Events</h3>
        <div className="filterSection">
          <div>
            <p onClick={() => setEventTypeFilter("All")}>All</p>
            <p onClick={() => setEventTypeFilter("single-day")}>
              Single-day event
            </p>
            <p onClick={() => setEventTypeFilter("multi-day")}>
              Multi-day event
            </p>
          </div>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            sx={{
              boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
              "& fieldset": { border: "none" },
              backgroundColor: "#FFF",
            }}
            value={timePeriod}
            onChange={handleTimePeriodChange}
          >
            <MenuItem value="Last 7 days">Last 7 days</MenuItem>
            <MenuItem value="Last 30 days">Last 30 days</MenuItem>
          </Select>
        </div>
        <section className="eventsList">
          {top5Events.map((event) => (
            <div className="eventItem" key={event.id}>
              <div className="eventTileAndLogo">
                <BsFillCalendarCheckFill />
                <div>
                  <p>{event.name}</p>
                  <span>{event.eventType} event</span>
                </div>
              </div>
              <p>{event.registrationCount} </p>
            </div>
          ))}
        </section>
        <section className="teamMembersList">
          <p>Team Members List</p>
          <div className="membersList">
            {dashboardData.users.map((e: IUser) => {
              return (
                <div className="userAvatar" key={e.id}>
                  <FaUserCircle />
                  <p>
                    {e.first_name} {e.last_name}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </Container>
  );
};

export default Dashboard;
